// REUSABLE: API Call Hook
// Custom React hook for making API calls with loading and error states. Used by: All components making API requests
// Benefits: Reduces boilerplate code (no more manual setLoading, setError), Consistent error handling across app, Automatic loading state management

import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';

interface ApiResponse<T = any> {
  statusCode: number;
  message: string;
  data?: T;
}

interface UseApiCallResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  execute: (apiFunction: () => Promise<ApiResponse<T>>) => Promise<{ success: boolean; data?: T; error?: string }>;
  reset: () => void;
}

export function useApiCall<T = any>(options?: {
  showSuccessToast?: boolean;
  showErrorToast?: boolean;
}): UseApiCallResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { showSuccessToast = false, showErrorToast = true } = options || {};

  const execute = useCallback(
    async (apiFunction: () => Promise<any>) => {
      setLoading(true);
      setError(null);

      try {
        const axiosResponse = await apiFunction();
        
        // Handle Axios response structure - Axios wraps the response
        // axiosResponse = { data: {...}, status: 200, ... }
        const response = axiosResponse.data || {};
        const statusCode = response.statusCode || axiosResponse.status || 200;

        // Success responses (200, 201, 202)
        if (
          statusCode === 200 ||
          statusCode === 201 ||
          statusCode === 202
        ) {
          // Backend wraps data in response.data, so we need response.data.data
          const responseData = response.data !== undefined ? response.data : response;
          setData(responseData || null);
          
          if (showSuccessToast && response.message) {
            toast.success(response.message);
          }
          
          return { success: true, data: responseData };
        } else {
          // Error responses (400, 404, 409, 500, etc.)
          const errorMsg = response.message || 'An error occurred';
          setError(errorMsg);
          
          if (showErrorToast) {
            toast.error(errorMsg);
          }
          
          return { success: false, error: errorMsg };
        }
      } catch (err: any) {
        const errorMsg = err.response?.data?.message || err.message || 'An unexpected error occurred';
        setError(errorMsg);
        
        if (showErrorToast) {
          toast.error(errorMsg);
        }
        
        return { success: false, error: errorMsg };
      } finally {
        setLoading(false);
      }
    },
    [showSuccessToast, showErrorToast]
  );

  const reset = useCallback(() => {
    setData(null);
    setLoading(false);
    setError(null);
  }, []);

  return { data, loading, error, execute, reset };
}

 // ========================================
 // REUSABLE: Form Submit Hook
 // ========================================
 // Specialized hook for form submissions with validation
 // @example
 // const { submitting, submit } = useFormSubmit();
 // const handleSubmit = async () => {
 // const result = await submit(
 // () => validateForm(formData),
 // () => createTimesheet(formData)
 // );
 // if (result.success) {
 // closeModal();
 // }
 // };

export function useFormSubmit() {
  const [submitting, setSubmitting] = useState(false);

  const submit = useCallback(
    async <T = any>(
      validate: () => { valid: boolean; errors?: Record<string, string> },
      apiFunction: () => Promise<ApiResponse<T>>
    ): Promise<{ success: boolean; data?: T; errors?: Record<string, string> }> => {
      // Validate first
      const validation = validate();
      
      if (!validation.valid) {
        return {
          success: false,
          errors: validation.errors,
        };
      }

      // Submit
      setSubmitting(true);

      try {
        const response = await apiFunction();

        if (
          response.statusCode === 200 ||
          response.statusCode === 201 ||
          response.statusCode === 202
        ) {
          toast.success(response.message || 'Operation successful');
          return { success: true, data: response.data };
        } else {
          toast.error(response.message || 'Operation failed');
          return { success: false };
        }
      } catch (err: any) {
        const errorMsg = err.message || 'An unexpected error occurred';
        toast.error(errorMsg);
        return { success: false };
      } finally {
        setSubmitting(false);
      }
    },
    []
  );

  return { submitting, submit };
}


